<template>
  <no-ssr>
    <div>
      <div class="product-configurator">
        <div class="product-configurator-head">
          Ihr Zaunkonfigurator
          <span>Wählen Sie die Optionen für Ihren Wunschzaun:</span>
        </div>

        <ul v-if="bullets">
          <li v-for="bullet in bullets" :key="bullet" v-html="bullet" />
        </ul>

        <div class="product-configurator-content">
          <div v-if="getCurrentProduct.drahtstaerke && getCurrentProduct.drahtstaerke_link_sku">
            <div
              :class="{
                'product-configurator-option': true,
                'option-wire-strength': true,
                'strong': fenceWireStrength === '868'
              }"
            >
              <div class="row">
                <label :id="'wire-strength-label'" class="configurator-option-head col-xs-4">
                  Drahtstärke
                </label>
                <div
                  class="configurator-option-content col-xs-8"
                  :id="'wire-strength-group'"
                  role="group"
                  :aria-labelledby="'wire-strength-label'"
                >
                  <button
                    v-for="(strength, index) in ['656', '868']"
                    :key="index"
                    :id="'wire-strength-' + strength"
                    type="button"
                    :class="{ active: getCurrentProduct.drahtstaerke === strength, 'btn-check': true }"
                    @click="switchToLinkedProduct(getCurrentProduct.drahtstaerke_link_sku)"
                    @mouseover="()=>{ fenceWireStrength = strength }"
                    @mouseleave="fenceWireStrength = getCurrentProduct.drahtstaerke"
                    :aria-pressed="getCurrentProduct.drahtstaerke === strength"
                  >
                    {{ strength }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="variants" v-if="getCurrentProduct.type_id =='configurable'">
            <configurable-options :product="getCurrentProduct" v-if="getCurrentProduct.drahtstaerke && getCurrentProduct.drahtstaerke_link_sku" />

            <div class="product-configurator-option-wrapper" v-else>
              <div
                class="product-configurator-option"
                v-for="(option, index) in getCurrentProduct.configurable_options"
                :key="index"
              >
                <div class="row">
                  <label
                    class="configurator-option-head col-xs-4"
                    data-testid="variantsLabel"
                  >
                    {{ option.label }}
                  </label>

                  <div class="configurator-option-content col-xs-8">
                    <div class="configurator-option-value" v-if="selectedHeight">
                      <span>{{ selectedHeight.label }}</span>
                    </div>
                    <div class="configurator-option-change">
                      <a
                        @click="$parent.openHeightSelector"
                        class="configurator-option-change"
                      >Höhe ändern</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <product-custom-options
            v-if="getCurrentProduct.custom_options && getCurrentProduct.custom_options.length > 0"
            :product="getCurrentProduct"
          />

          <div class="product-configurator-summary">
            <div class="configurator-summary-inner">
              <div class="configurator-summary-price">
                <div v-if="configuredPrice && configuredOldPrice">
                  <div class="price-old">
                    <span>{{ configuredOldPrice | price }}</span> *
                  </div>
                  <div class="price-offer discount">
                    {{ configuredPrice | price }}
                    <span
                      class="badge badge-sm badge-secondary"
                    >Sonderpreis</span>
                  </div>
                </div>
                <div class="price-offer" v-else-if="configuredPrice">
                  {{ configuredPrice | price }}
                </div>
                <product-base-price :product="{... getCurrentProduct, baseprice_product_amount: getCalculatedLength() + getAdditionalFenceLength() }" :price="configuredPrice" />

                <div class="configurator-summary-tax">
                  <span v-if="storeCode === 'zaun24_de' && getCurrentProduct.tax_class_id === 1">inkl. 19% MwSt.,</span>
                  <span v-if="storeCode === 'zaun24_de' && getCurrentProduct.tax_class_id === 13">umsatzsteuerbefreit, gemäß §12 Abs. 3 UstG. *</span>
                  <span v-if="storeCode === 'zaun24_at' && getCurrentProduct.tax_class_id === 13">umsatzsteuerbefreit, gemäß § 28 Abs. 62 UstG. *</span>
                  <span v-else-if="storeCode === 'zaun24_at'">inkl. 20% MwSt.,</span>
                  <br>
                  <span class="product-delivery-time" v-if="getCurrentProduct.delivery_time"><router-link :to="localizedRoute('/lieferzeit')">{{ $t('Shipping Time') }} {{ getCurrentProduct.delivery_time }}</router-link>&nbsp;&nbsp;&middot;&nbsp;</span><matrixrates :product="getCurrentProduct" />
                </div>
              </div>

              <div
                class="configurator-summary-quantity"
                v-if="getCurrentProduct.type_id !== 'grouped' && getCurrentProduct.type_id !== 'bundle'"
              >
                <product-quantity
                  class="row m0 mb35"
                  v-if="getCurrentProduct.type_id !== 'grouped' && getCurrentProduct.type_id !== 'bundle'"
                  v-model="getCurrentProduct.qty"
                  :max-quantity="$parent.maxQuantity"
                  :loading="$parent.isStockInfoLoading"
                  :is-simple-or-configurable="$parent.isSimpleOrConfigurable"
                  :show-quantity="$parent.manageQuantity"
                  @error="$parent.handleQuantityError"
                />
              </div>
            </div>

            <div class="configurator-summary-action">
              <add-to-cart :product="getCurrentProduct" :disabled="$parent.isAddToCartDisabled" />
            </div>
          </div>
        </div>
      </div>
      <!-- zaun24-financing ref="financing" :price="configuredPrice" v-if="storeCode === 'zaun24_de'" / -->
    </div>
  </no-ssr>
</template>

<script>
import { mapActions } from 'vuex'
import AddToCart from 'theme/components/core/AddToCart.vue'
import ProductCustomOptions from 'theme/components/core/ProductCustomOptions.vue'
import ProductQuantity from 'theme/components/core/ProductQuantity.vue'
import NoSSR from 'vue-no-ssr'
import ConfigurableOptions from 'theme/components/core/blocks/Product/ConfigurableOptions'
import ProductBasePrice from 'theme/components/core/blocks/Product/BasePrice'
import Matrixrates from 'src/modules/vsf-matrixrates/components/Matrixrates'
import { storeCode as getStoreCode } from '@vue-storefront/core/lib/multistore'
import { parsePriceMatrix, getSortedLengths } from 'theme/helpers/fence'
import { prepareRelatedQuery } from '@vue-storefront/core/modules/catalog/queries/related'
import { formatProductLink } from '@vue-storefront/core/modules/url/helpers'

const DYNAMIC_FILTER_REGEX = /^\[(.+?):(.+?)\](.+)$/gm
const LENGTH_OPTION_TITLE_REGEX = /(zaunlänge|laenge|länge)/i
const COLOR_OPTION_TITLE_REGEX = /zaunfarbe/i
const DEFAULT_INTRO_TEMPLATE = '<strong>{{length}} Meter {{name}}, {{height}} Höhe, {{color}}</strong></div><div style="margin-bottom:10px;">Bestehend aus:</div>'
const MAX_LENGTH = 9999

export default {
  props: {
    selectedHeight: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    AddToCart,
    ProductCustomOptions,
    Matrixrates,
    ProductQuantity,
    ProductBasePrice,
    ConfigurableOptions,
    // Zaun24Financing: () => import('theme/components/core/blocks/Custom/Zaun24Financing.vue'),
    'no-ssr': NoSSR
  },
  data () {
    return {
      fenceWireStrength: null
    }
  },
  watch: {
    getCurrentProduct (currentProduct) {
      this.suppliesSetCurrentProduct(currentProduct)
    }
  },
  computed: {
    getCurrentProduct () {
      return this.$parent.getCurrentProduct
    },
    storeCode () {
      return getStoreCode()
    },
    configuredPrice () {
      let price = 0
      price += this.getMatrixPrice()
      const step = this.getPriceStep()
      if (Number.isFinite(step) && step > 0) {
        price += (this.getAdditionalElementsPrice() * this.getAdditionalLength()) / step
      }
      return Number.isFinite(price) ? price : 0
    },
    configuredOldPrice () {
      const discountFactor = this.toFiniteNumber(this.getCurrentProduct.discount_factor)
      if (discountFactor <= 0) {
        return 0
      }
      return Math.ceil(this.configuredPrice / discountFactor).toFixed(2)
    },
    description () {
      const fenceIntro = this.getCurrentProduct.fence_dynamic_intro || DEFAULT_INTRO_TEMPLATE
      const dynamicDescription = this.listify(this.filterDynamicDescription(this.getCurrentProduct.fence_dynamic_description))
      const desc = `${fenceIntro}${dynamicDescription}${this.getCurrentProduct.description || ''}`
      return this.evalTemplate(desc)
    },
    bullets () {
      const bulletText = this.evalTemplate(this.filterDynamicDescription(this.getCurrentProduct.fence_dynamic_bullets))
      return bulletText.split('\n').filter(line => line.trim() !== '')
    },
    templateVars () {
      const product = this.getCurrentProduct || {}
      const color = this.getColor() || {}
      const rawLength = this.getLength()
      const length = this.toFiniteNumber(this.getCalculatedLength()) + this.toFiniteNumber(this.getAdditionalFenceLength())
      const width = this.getFenceWidth()
      const elements = width > 0 ? Math.round(length / width) : 0
      const normalizedElements = Number.isFinite(elements) ? elements : 0
      const firstOption = Array.isArray(product.options) ? product.options[0] : null
      const height = firstOption && firstOption.value != null ? firstOption.value : ''

      return {
        ...product,
        length,
        length_raw: String(this.toFiniteNumber(rawLength)).replace('.', ','),
        element_height: 1230,
        pillars_length: 2345,
        name: product.name,
        sku: color.sku || '',
        height,
        color: color.title || '',
        elements: normalizedElements,
        pillars: normalizedElements + 1
      }
    }
  },
  methods: {
    ...mapActions({
      suppliesSetCurrentProduct: 'supplies/setCurrentProduct'
    }),
    async switchToLinkedProduct (sku) {
      const relatedProductsQuery = prepareRelatedQuery('sku', sku)
      const { items } = await this.$store.dispatch('product/findProducts', {
        query: relatedProductsQuery,
        size: 1
      })
      const linkedProduct = items && items[0]
      if (!linkedProduct) {
        return
      }

      const scrollY = window.scrollY
      this.$router.push(
        formatProductLink(linkedProduct, this.storeCode)
      )

      setTimeout(() => {
        window.scrollTo(0, scrollY)
        this.applyLastFenceHeightOption()
      }, 50)
    },
    getParentFenceHeightOptions () {
      const parent = this.$parent
      if (!parent || !parent.$refs || !parent.$refs.fenceHeightSelector) {
        return []
      }

      const options = parent.$refs.fenceHeightSelector.options
      return options && options.length ? options : []
    },
    applyLastFenceHeightOption () {
      const parent = this.$parent
      const options = this.getParentFenceHeightOptions()
      if (!parent || !options.length) {
        return
      }

      parent.selectedHeight = options[options.length - 1].value
    },
    getFencePrices () {
      return parsePriceMatrix(this.getCurrentProduct.price_matrix_fence)
    },
    getSortedLengths () {
      return getSortedLengths(this.getFencePrices())
    },
    getElementsPrice (color) {
      const elementPrices = parsePriceMatrix(
        this.getCurrentProduct.price_matrix_elements
      )
      const firstRow = Object.values(elementPrices).find(() => true)
      if (!firstRow || !color) {
        return undefined
      }
      return firstRow[color]
    },
    listify (str) {
      if (!str) {
        return ''
      }
      return '<ul><li>' +
        str.split('\n').filter((v) => v.trim() !== '').join('</li><li>') +
        '</li></ul>'
    },
    filterDynamicDescription (desc) {
      if (!desc || typeof desc !== 'string') {
        return ''
      }
      return desc.replace(DYNAMIC_FILTER_REGEX, this.filterCallback)
    },
    filterCallback (match, key, value, textContent, offset, original) {
      if (this.templateVars[key] === value) {
        return textContent
      }
      return ''
    },
    evalTemplate (template) {
      if (!template || typeof template !== 'string') {
        return ''
      }
      for (const [key, value] of Object.entries(this.templateVars)) {
        template = template.replace(
          new RegExp('{{' + key + '}}', 'g'),
          value
        )
      }
      return template
    },
    getAdditionalElementsPrice () {
      const color = this.getColor()
      if (!color) {
        return 0
      }
      return this.toFiniteNumber(this.getElementsPrice(color.sku))
    },
    getMatrixPrice () {
      const lengthConfig = this.getLengthConfig()
      const color = this.getColor()
      if (!color || !lengthConfig) {
        return 0
      }
      return this.toFiniteNumber(lengthConfig[color.sku])
    },
    getLengthConfig () {
      const matrixLen = this.getMatrixLength(null)
      if (!Number.isFinite(matrixLen)) {
        return undefined
      }
      const matrix = this.getFencePrices()
      const fixed = matrixLen.toFixed(2)
      const compact = String(parseFloat(fixed))
      return matrix[fixed] || matrix[compact] || matrix[String(matrixLen)]
    },
    getCalculatedLength () {
      let length = this.getLength()
      const fenceWidth = this.toFiniteInt(this.getCurrentProduct.fence_width)
      if (fenceWidth > 0) {
        length =
          length % fenceWidth === 0
            ? length
            : this.intervalize(length, fenceWidth)
      }
      return this.getMatrixLength(length)
    },
    getMatrixLength (length) {
      if (length === null) {
        length = this.getLength()
      }
      const keys = this.getSortedLengths()
      if (!keys.length) {
        return 0
      }
      if (length === 0) {
        if (this.getCurrentProduct.fence_default_length) {
          const d = this.parseLocalizedFloat(this.getCurrentProduct.fence_default_length)
          return Number.isFinite(d) ? d : parseFloat(keys[0])
        }
        return parseFloat(keys[0])
      }

      let cfg = null
      for (let i = 0; i < keys.length; i++) {
        const _length = keys[i]
        if (_length >= length) {
          cfg = _length
          break
        }
      }
      if (cfg === null) {
        const last = parseFloat(keys[keys.length - 1])
        return Number.isFinite(last) ? last : 0
      }
      const out = parseFloat(cfg)
      return Number.isFinite(out) ? out : 0
    },
    getColor () {
      const option = this.getSelectedOptionByType(['select', 'drop_down'], COLOR_OPTION_TITLE_REGEX)
      if (option.values) {
        return option.values.find(
          val => val.option_type_id === this.toFiniteInt(option.option_value)
        )
      }
      return null
    },
    getSelectedOptionByType (type, titleMatcher = null) {
      const types = Array.isArray(type) ? type : [type]
      const customOptions = (this.getCurrentProduct && this.getCurrentProduct.custom_options) || []

      let option = customOptions.find(opt => types.includes(opt.type))
      if (titleMatcher) {
        option = customOptions.find(
          opt => types.includes(opt.type) && typeof opt.title === 'string' && titleMatcher.test(opt.title)
        ) || option
      }
      if (!option) {
        return {}
      }

      const currentCustomOptions = this.$store.state.product.current_custom_options || {}
      const current = currentCustomOptions[option.option_id] || {}
      return { ...option, ...current }
    },
    getLength () {
      const selectedOption = this.getSelectedOptionByType('field', LENGTH_OPTION_TITLE_REGEX)
      const raw = selectedOption && selectedOption.option_value
      if (raw === null || raw === undefined || raw === '') {
        return 0
      }
      return Math.min(this.parseLocalizedFloat(raw), MAX_LENGTH)
    },
    getFenceWidth () {
      const width = this.toFiniteInt(this.getCurrentProduct.fence_width)
      if (width > 0) {
        return width
      }
      return this.getPriceStep()
    },
    getPriceStep () {
      const steps = this.getSortedLengths()
      if (steps.length >= 2) {
        const a = parseFloat(steps[0])
        const b = parseFloat(steps[1])
        const d = b - a
        if (Number.isFinite(d) && d > 0) {
          return d
        }
      }
      const fw = this.toFiniteInt(this.getCurrentProduct.fence_width)
      if (Number.isFinite(fw) && fw > 0) {
        return fw
      }
      return 1
    },
    getAdditionalFenceLength () {
      const step = this.getFenceWidth()
      return this._getAdditionalLength(step)
    },
    getAdditionalLength () {
      const step = this.getPriceStep()
      return this._getAdditionalLength(step)
    },
    _getAdditionalLength (step) {
      const additionalLength = this.getLength() - this.getCalculatedLength()
      if (additionalLength > 0 && Number.isFinite(step) && step > 0) {
        return this.intervalize(additionalLength, step)
      }
      return 0
    },
    intervalize (num, step) {
      return Math.ceil(num / step) * step
    },
    getMinimumLength () {
      const steps = this.getSortedLengths()
      if (!steps.length) {
        return 0
      }
      const first = parseFloat(steps[0])
      const fw = Math.floor(this.getFenceWidth() - 0.1)
      return (Number.isFinite(first) ? first : 0) - (Number.isFinite(fw) ? fw : 0)
    },
    parseLocalizedFloat (value) {
      const parsed = parseFloat(typeof value === 'number' ? String(value) : String(value).replace(',', '.'))
      return Number.isFinite(parsed) ? parsed : 0
    },
    toFiniteNumber (value) {
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    toFiniteInt (value) {
      const parsed = parseInt(String(value), 10)
      return Number.isFinite(parsed) ? parsed : 0
    }
  }
}
</script>
